import { HashRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { ThemeProvider, useTheme } from "@/hooks/use-theme";
import { isAuthenticated } from "@/lib/auth";
import { MainLayout } from "@/components/layout/MainLayout";
import LoginPage from "@/pages/LoginPage";
import OverviewPage from "@/pages/OverviewPage";
import TransportsPage from "@/pages/config/TransportsPage";
import AuthsPage from "@/pages/config/AuthsPage";
import AorsPage from "@/pages/config/AorsPage";
import IdentifiesPage from "@/pages/config/IdentifiesPage";
import ContextsPage from "@/pages/config/ContextsPage";
import ContextDetailPage from "@/pages/config/ContextDetailPage";
import ExtensionsPage from "@/pages/config/ExtensionsPage";
import AriAppsPage from "@/pages/config/AriAppsPage";
import WebsocketEndpointsPage from "@/pages/config/WebsocketEndpointsPage";
import HepSettingsPage from "@/pages/config/HepSettingsPage";
import ConfigViewPage from "@/pages/config/ConfigViewPage";
import ActiveCallsPage from "@/pages/calls/ActiveCallsPage";
import CallHistoryPage from "@/pages/calls/CallHistoryPage";
import AuditLogsPage from "@/pages/settings/AuditLogsPage";
import AmiConsolePage from "@/pages/admin/AmiConsolePage";
import ApiKeysPage from "@/pages/admin/ApiKeysPage";
import LogsPage from "@/pages/LogsPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} />;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <HashRouter>
        <Routes>
          <Route path="login" element={<LoginPage />} />
          <Route
            element={
              <RequireAuth>
                <MainLayout />
              </RequireAuth>
            }
          >
            <Route index element={<OverviewPage />} />
            <Route path="config" element={<Navigate to="/config/contexts" replace />} />
            <Route path="config/contexts" element={<ContextsPage />} />
            <Route path="config/contexts/:id" element={<ContextDetailPage />} />
            <Route path="config/transports" element={<TransportsPage />} />
            <Route path="config/auths" element={<AuthsPage />} />
            <Route path="config/aors" element={<AorsPage />} />
            <Route path="config/identifies" element={<IdentifiesPage />} />
            <Route path="config/extensions" element={<ExtensionsPage />} />
            <Route path="config/ari-apps" element={<AriAppsPage />} />
            <Route path="config/websocket-endpoints" element={<WebsocketEndpointsPage />} />
            <Route path="config/hep" element={<HepSettingsPage />} />
            <Route path="config/view" element={<ConfigViewPage />} />
            <Route path="calls/active" element={<ActiveCallsPage />} />
            <Route path="calls/history" element={<CallHistoryPage />} />
            <Route path="admin/ami-console" element={<AmiConsolePage />} />
            <Route path="admin/logs" element={<LogsPage />} />
            <Route path="admin/config" element={<ConfigViewPage />} />
            <Route path="admin/audit-logs" element={<AuditLogsPage />} />
            <Route path="admin/api-keys" element={<ApiKeysPage />} />
            <Route path="admin" element={<Navigate to="/admin/ami-console" replace />} />
          </Route>
        </Routes>
        </HashRouter>
        <ThemedToaster />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;